import { getNoteState } from "./helper";
import { useTranslation } from "react-i18next";
import NoteModel from "../../api/models/NoteModel";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { classNames } from "../../helpers";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { removeNotes, updateTutorNote } from "../../api/endpoints/tutors/notes";
import TableHeaderCell, { SortDirection } from "../table-cell/TableHeaderCell";
type NoteInfoTableProps = {
  notes: NoteModel[];
  getSession?: any;
  sessionId?: any;
  getSortingDirection?: (colName: string) => SortDirection | undefined;
  handleSetSortingOrder?: (colName: string) => () => void;
};

export default function NoteInfoTable({ notes }: NoteInfoTableProps) {
  const LocalBasePath = "page.student.dashboard.sessionInfo";
  const { t } = useTranslation();
  return (
    <>
      <div
        className="ring-1 ring-gray-300 md:mx-0 md:rounded-lg"
        data-cy="studentNotesTable"
      >
        <table className="min-w-full divide-y table-fixed divide-gray-300 ">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3  text-justify text-sm font-semibold text-gray-500 sm:pl-6">
                {t(`${LocalBasePath}.note`)}
              </th>
              <th
                scope="col"
                className=" px-3 w-[128px]  py-3.5 text-justify text-sm font-semibold text-gray-500 lg:table-cell"
              >
                {t(`${LocalBasePath}.status`)}
              </th>
            </tr>
          </thead>
          <tbody>
            {notes.map((note, index) => {
              return (
                <tr
                  key={`NoteInfoTable_${index}_note`}
                  className={" border-t "}
                  data-cy="studentNote"
                  data-cy-note-status={note.status}
                >
                  <td className={"relative py-4 pl-4 sm:pl-6 pr-3 text-sm"}>
                    <div className="font-medium text-gray-500">{note.note}</div>
                  </td>
                  <td
                    className={
                      " px-3 py-3.5 text-sm text-gray-500 lg:table-cell"
                    }
                  >
                    {getNoteState(note?.status!)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function TutorNoteInfoTable({
  notes,
  getSession,
  sessionId,
  getSortingDirection = () => undefined,
  handleSetSortingOrder = () => () => {},
}: NoteInfoTableProps) {
  const LocalBasePath = "page.tutor.dashboard.sessionInfo";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const checkbox = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<NoteModel[]>([]);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedNotes.length > 0 && selectedNotes.length < notes.length;
    setChecked(selectedNotes.length === notes.length);
    setIndeterminate(isIndeterminate);
    if (!!checkbox.current) checkbox.current.indeterminate = isIndeterminate;
  }, [selectedNotes]);

  useEffect(() => {
    setSelectedNotes([]);
  }, [notes]);
  function toggleAll() {
    setSelectedNotes(checked || indeterminate ? [] : notes);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }
  const UpdateNoteSate = async (state: string, note: NoteModel) => {
    /**
     * on session room  note return with session ID,
     * reading room  notes not return with session ID, return note ID
     * update note state on server need to pass session ID
     * so we need to get session ID to update note state
     */
    const updatedNoteResponse = await updateTutorNote(note.id, {
      session_id: note.session?.id ?? sessionId,
      state: state,
    });
    if (updatedNoteResponse.isSuccess) getSession(sessionId);
  };
  const removeNote = async (note: NoteModel) => {
    const removeResponse = await removeNotes(note.id);
    if (removeResponse.isSuccess) getSession(sessionId);
  };
  return (
    <div className="ring-1 ring-gray-300 md:mx-0 md:rounded-lg relative">
      {selectedNotes.length > 0 && (
        <div className="absolute top-0 start-16 flex h-12 items-center gap-3 bg-gray-50 sm:start-20">
          <button
            type="button"
            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {t("editAll")}
          </button>
          <button
            type="button"
            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {t("deleteAll")}
          </button>
        </div>
      )}
      <table className="min-w-full divide-y table-fixed divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <TableHeaderCell>{t("note")}</TableHeaderCell>
            <TableHeaderCell
              className="w-[128px] text-center"
              sortable={true}
              sortingDirection={getSortingDirection!("state")}
              onClick={handleSetSortingOrder!("state")}
              textPosition="center"
            >
              {t("status")}
            </TableHeaderCell>
            <TableHeaderCell>
              <span className="sr-only">More options</span>
            </TableHeaderCell>
          </tr>
        </thead>
        <tbody data-cy="tutorNotesTable">
          {notes.map((note, index) => {
            return (
              <tr
                key={`NoteInfoTable_${index}_note`}
                data-cy="noteInfoRow"
                className={classNames(
                  "border-b",
                  selectedNotes.includes(note) && "bg-gray-50"
                )}
              >
                <td className={"relative py-2 ps-4 sm:ps-6 pe-3 text-sm"}>
                  <div
                    className={`font-medium ${
                      selectedNotes.includes(note)
                        ? "text-brandblue-500"
                        : "text-gray-500"
                    }`}
                  >
                    {note.note}
                  </div>
                </td>
                <td
                  className={
                    " px-3 py-2 text-xs text-gray-400 lg:table-cell text-center"
                  }
                >
                  {getNoteState(note?.status!)}
                </td>
                <td className=" pe-4 h-2 w-2 px-4 text-gray-400 sm:pe-6 ps-3">
                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button
                        data-cy="updateStatus"
                        className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-brandblue-500"
                      >
                        <DotsVerticalIcon
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={"div"}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                      dir={"ltr"}
                    >
                      {/* Arrow */}
                      <div className="z-[100] w-4 h-4 border border-l-0 border-b-0 border-black border-opacity-5 absolute ltr:right-[100%] rtl:left-[100%] -top-[0.75rem] -translate-y-full ltr:rotate-45 rtl:rotate-[225deg] bg-white focus:outline-none" />
                      <Menu.Items className="z-[99] absolute ltr:right-[120%] rtl:left-[120%] -top-[0.5rem] -translate-y-full mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="p-2 cursor-pointer">
                          <Menu.Item>
                            <button
                              data-cy="updateStatusPassed"
                              className="w-full"
                              onClick={() => UpdateNoteSate("passed", note)}
                            >
                              <h5>{t("passed")}</h5>
                            </button>
                          </Menu.Item>
                        </div>
                        <div className="p-2  cursor-pointer">
                          <Menu.Item>
                            <button
                              className="w-full"
                              data-cy="updateStatusUnPassed"
                              onClick={() => UpdateNoteSate("unpassed", note)}
                            >
                              <h5>{t("unpassed")}</h5>
                            </button>
                          </Menu.Item>
                        </div>
                        <div className="p-2  cursor-pointer">
                          <Menu.Item>
                            <button
                              className="w-full"
                              data-cy="updateStatusUnPassed"
                              onClick={() => removeNote(note)}
                            >
                              <h5>{t("delete")}</h5>
                            </button>
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
